import { getAnalytics, setUserProperties } from 'firebase/analytics'
import { isString } from 'fitify-types/src/type-guards/common'
import { useRunOnce } from 'fitify-ui/src/hooks/useRunOnce'
import { useEffect, useMemo } from 'react'
import smartlookClient from 'smartlook-client'

import type { Route } from '../types/routeTypes'
import { logEvent } from '../utils/analytics'
import { filterUndefinedValues } from '../utils/helpers'
import { getRouteEventName } from '../utils/routes'

import { useRouter } from './useRouter'

/**
 * USER PROPERTIES
 */
export function useUserProperties(data: Record<string, any>) {
  useEffect(() => {
    // Early return if data do not exists
    if (!data) return

    // Define property mappings for user properties
    const propertyMappings = [
      {
        key: 'gender',
        source: ['profile.gender', 'gender'],
        map: {
          m: 'MALE',
          f: 'FEMALE',
          u: 'UNKNOWN',
          n: 'NON_BINARY',
        },
      },
      { key: 'age', source: ['profile.age', 'age'] },
      { key: 'weight', source: ['profile.weight', 'weight'] },
      { key: 'height', source: ['profile.height', 'height'] },
      {
        key: 'goal',
        source: ['profile.goal', 'goal'],
        map: {
          1: 'LOSE_FAT',
          2: 'GET_FITTER',
          3: 'GAIN_MUSCLE',
          6: 'LOSE_AND_GAIN',
        },
      },
      {
        key: 'fitness',
        source: ['profile.fitness', 'fitness'],
        map: {
          1: 'NOT_VERY_FIT',
          2: 'FIT',
          3: 'VERY_FIT',
        },
      },
      { key: 'max_impact', source: ['profile.max_impact', 'max_impact'] },
    ] as {
      key: string
      source: string[]
      map?: Record<string | number, any>
    }[]

    // Initialize an object to store user properties
    const userProperties: Record<string, any> = {}

    // Process property mappings
    for (const { key, source, map } of propertyMappings) {
      for (const prop of source) {
        const dataValue = prop.split('.').reduce((obj, key) => {
          const value = map ? map[obj?.[key]] : obj?.[key]
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return value
        }, data)

        if (dataValue) {
          userProperties[key] = dataValue
          break
        }
      }
    }

    // Filter out undefined properties and check if there are properties to track
    const filteredUserProperties = filterUndefinedValues(userProperties)

    // Set user properties in firebase and smartlook
    setUserProperties(getAnalytics(), filteredUserProperties, { global: true })
    smartlookClient.properties(filteredUserProperties)
  }, [data])
}

/**
 * PAGE EVENTS
 */
export function usePageEvent(routes: Route<string>[]) {
  const router = useRouter()

  useEffect(() => {
    const page = routes.find((page) => page.path === router.resolvedPathname)
    if (!page) return

    const pageEvent = getRouteEventName(page.id)
    if (pageEvent) {
      logEvent(pageEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.resolvedPathname])
}

export const useRedirectExperimentEvent = () => {
  const router = useRouter()

  const eventParams = useMemo(() => {
    const experimentId = router.query.redirect_experiment
    const variationId = router.query.redirect_variation

    if (isString(experimentId) && isString(variationId)) {
      return {
        experiment_id: experimentId,
        variation_id: variationId,
      }
    }

    return undefined
  }, [router.query])

  useRunOnce(
    () => {
      logEvent('redirect_experiment_impression', eventParams)
    },
    Boolean(router.isReady && eventParams)
  )
}
